import axiosInstance from 'config/axiosInstance';
import { formatBusiness } from 'config/time';
import { fetchAll } from 'config/fetchAll';
import React, { useEffect, useRef, useState } from 'react';
import { IconEye, IconEdit } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import EditPopup from 'reusable/AddEditDelete/Edit';
import { useTranslation } from 'react-i18next';
import { useClassifiedErrorMessages, useSaveError } from 'reusable/useSaveError';
import { deviceAssignmentMessages } from 'config/apiErrors';
import { assignableDevices } from './deviceCandidates';

/** 409 assignment_conflict（openapi-schema.yaml の AssignmentConflict）か。 */
const isAssignmentConflict = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })
    ?.response;
  return response?.status === 409 && response.data?.code === 'assignment_conflict';
};

export interface DriverInterface {
  id: number;
  name: string;
  submission: number;
  exceed: number;
  phone: string;
  email: string;
  birthday: string;
  create_time: string;
  profile_picture?: string;
  client_delivery?: number;
  device: number[];
}

interface DriverTableProps {
  driver: DriverInterface[];
  /** ADM-14。保存が途中で失敗したとき、一覧をサーバーの確定状態で取り直す。 */
  onChanged?: () => void;
}

const DriverTable: React.FC<DriverTableProps> = ({ driver, onChanged }) => {
  const { t, i18n } = useTranslation('driver');
  const { showSaveError, showMessages, saveErrorPopup } = useSaveError();
  const classifiedMessages = useClassifiedErrorMessages();
  // EV-318。機器の候補を読めなかったとき（候補が無いことと区別して、機器の欄に出す）。
  const [devicesError, setDevicesError] = useState<unknown>(null);
  const navigate = useNavigate();
  const [selectedDriver, setSelectedDriver] = useState<DriverInterface | null>(
    null
  );
  const [drivers, setDrivers] = useState<DriverInterface[]>(driver);
  const [devices, setDevices] = useState<any[]>([]);
  const [assignedDevices, setAssignedDevices] = useState<any[]>([]);
  // PR #99 レビュー。サーバーは devices を「割当の完全な一覧」として扱う。割当済みの機器を
  // 読めていないまま選ばせて送ると、見えていなかった既存の割当が黙って外れる。
  // 読めたときだけ機器欄を使えるようにし、devices を送る。
  const [assignedStatus, setAssignedStatus] = useState<'loading' | 'loaded' | 'failed'>(
    'loading'
  );

  const handleViewClick = (id: number) => {
    navigate(`/driver/${id}`);
  };

  // PR #99 レビュー。編集画面を開くたび（閉じたときも）番号を進め、いま開いている
  // 編集画面の要求の応答だけを使う。A を開いて閉じ、B を開いた後に A の遅れた応答が
  // 届くと、B の割当（保存の基準）が A のもので上書きされ、B の割当が黙って外れ得た。
  const editRequest = useRef(0);
  // PR #99 レビュー。編集画面を開いた（閉じた）回数。保存の結果は、その保存を始めた
  // 編集画面がまだ開いているときだけ反映する。A の保存を待つ間に閉じて B を開くと、
  // 後から届いた A の結果（競合の読み直し・閉じる・再読み込み・知らせ）が B に及んでいた。
  // 割当の読み直し（editRequest）とは分ける（読み直しでは保存中の画面は変わらない）。
  const editSession = useRef(0);

  /**
   * 割当済みの機器（保存の基準）を読む。読めなかったときは基準を変えずに 'failed'。
   * 後から開いた（閉じた）編集画面に追い越されたら 'superseded'（何も変えない）。
   * PR #99 レビュー。保存の競合の後は、ほかの入力を保ったまま基準だけを読み直す。
   */
  const loadAssignedBase = async (
    driver: DriverInterface
  ): Promise<'loaded' | 'failed' | 'superseded'> => {
    const request = ++editRequest.current;
    setAssignedStatus('loading');
    const devices = await fetchAssignedDevices(driver.id);
    if (request !== editRequest.current) return 'superseded';
    if (!devices) {
      // EV-032。読めなかったときは、開いたときに読めなかった場合と同じく機器を並べない
      // （古い割当や、競合の前に選んだ機器を出したままにしない）。devices は 'loaded' の
      // ときだけ送るので、基準を空にしても割当は変わらない。
      setAssignedDevices([]);
      setAssignedStatus('failed');
      return 'failed';
    }
    setAssignedDevices(devices);
    setAssignedStatus('loaded');
    return 'loaded';
  };

  const handleEditClick = async (driver: DriverInterface) => {
    editSession.current += 1;
    setAssignedDevices([]);
    setSelectedDriver(driver);
    await loadAssignedBase(driver);
  };
  const handleClosePopup = () => {
    editSession.current += 1;
    editRequest.current += 1;
    setSelectedDriver(null);
  };

  const handleSave = async (updatedValues: { [key: string]: any }) => {
    console.log(updatedValues);
    if (updatedValues['Driver Name'] === undefined) {
      updatedValues['Driver Name'] = selectedDriver?.name;
    }

    const driverNameEngChanged = updatedValues['DRIVER NAME'] !== undefined && updatedValues['DRIVER NAME'].trim() !== selectedDriver?.name.trim();
    const driverNameJpnChanged = updatedValues['ドライバー名'] !== undefined && updatedValues['ドライバー名'].trim() !== selectedDriver?.name.trim();
    const emailAddressEngChanged = updatedValues['EMAIL ADDRESS'] !== undefined && updatedValues['EMAIL ADDRESS'].trim() !== selectedDriver?.email.trim();
    const emailAddressJpnChanged = updatedValues['メールアドレス'] !== undefined && updatedValues['メールアドレス'].trim() !== selectedDriver?.email.trim();

    const driverNameChanged = driverNameEngChanged || driverNameJpnChanged;
    const emailAddressChanged = emailAddressEngChanged || emailAddressJpnChanged;

    if (driverNameChanged) {
        const newDriverName = updatedValues['DRIVER NAME']?.trim() || updatedValues['ドライバー名']?.trim();
        if (!newDriverName) {
            showMessages([t('ui.name-required', { ns: 'common' })]);
            return false;
        }
    }
    if (emailAddressChanged) {
        const newEmailAddress = updatedValues['EMAIL ADDRESS']?.trim() || updatedValues['メールアドレス']?.trim();
        if (!newEmailAddress) {
            showMessages([t('ui.email-required', { ns: 'common' })]);
            return false;
        }
    }
    
    // ADM-03。編集画面は割り当てを項目名（表示言語の文字）で返す。英語の固定文字で
    // 読むと日本語表示では空と扱われ、変えていない割り当てまで解除していた。
    const assignedNewDevices =
      updatedValues[t('edit.assigned-device')] ?? updatedValues['ASSIGNED DEVICE'] ?? [];
    const newDeviceIds = assignedNewDevices.map((device: any) => device.id);

    function getValueByLanguage<T>(object: T, englishKey: keyof T, japaneseKey: keyof T): T[keyof T] {
      return object[japaneseKey] ?? object[englishKey];
    }    

    const updatedDriver: DriverInterface = {
      id: updatedValues.id,
      name: getValueByLanguage(updatedValues, 'DRIVER NAME', 'ドライバー名'),
      submission: selectedDriver?.submission || 0,
      exceed: selectedDriver?.exceed || 0,
      phone: getValueByLanguage(updatedValues, 'CONTACT NUMBER', '電話番号'),
      email: getValueByLanguage(updatedValues, 'EMAIL ADDRESS', 'メールアドレス'),
      birthday: getValueByLanguage(updatedValues, 'DATE OF BIRTH', '生年月日'),
      create_time: updatedValues.create_time,
      profile_picture: '',
      device: newDeviceIds,
    };
    
    // ADM-14 / BE-02。機器の割当は運転者の更新に含めて送り、サーバーで1つの更新として
    // 検証・確定させる。以前は機器APIで1台ずつ書き換えており、途中で失敗すると一部だけが
    // 確定し、他の運転者が使用中の機器も黙って移せた。理由（device_in_use /
    // device_not_available）も受け取れなかった。
    // ADM-03。割当を変えていないときは devices を送らない（省略は「変更しない」）。
    // 割当済みの機器を読めなかったときに、空の選択で割当を消さないため。
    const assignedDeviceIds: number[] = assignedDevices.map((device: any) => device.id);
    const devicesChanged =
      assignedStatus === 'loaded' &&
      (newDeviceIds.length !== assignedDeviceIds.length ||
        newDeviceIds.some((id: number) => !assignedDeviceIds.includes(id)));

    const driverPatchBody = {
      name: updatedDriver.name,
      birthday: updatedDriver.birthday,
      phone: updatedDriver.phone,
      email: updatedDriver.email,
      // PR #99 レビュー。devices は割当の完全な一覧で置き換えるため、読み込んだ割当
      // （基準）を添える。他の管理者が先に変えていれば 409 assignment_conflict になり、
      // その変更を黙って取り消さない。
      ...(devicesChanged
        ? { devices: newDeviceIds, expected_devices: assignedDeviceIds }
        : {}),
    };

    const session = editSession.current;
    try {
      await axiosInstance.patch(
        `/v1/drivers/${updatedDriver.id}/`,
        driverPatchBody
      );
      // 保存を始めた編集画面が閉じられていたら、いま開いている画面を閉じない・
      // 再読み込みしない・知らせない（false で編集画面に「閉じない」と返す）。
      // サーバーの状態は変わったので、どの画面にも結び付かない一覧と機器の候補は
      // 読み直す（一覧が古いまま残らないように）。
      if (session !== editSession.current) {
        onChanged?.();
        fetchDeviceData();
        return false;
      }
      setDrivers((prevDrivers) =>
        prevDrivers.map((drv) =>
          drv.id === updatedDriver.id ? updatedDriver : drv
        )
      );
      setSelectedDriver(null);
      window.location.reload();
    } catch (error) {
      console.error('Failed to update driver', error);
      // 保存を始めた編集画面が閉じられていたら、結果を反映しない（知らせも出さない）。
      if (session !== editSession.current) return false;
      // 以前は失敗しても再読み込みして、理由が消えていた。
      // ADM-15。編集画面は閉じず（入力を残し）、理由を前面に出す。
      // BE-02。機器を割り当てられない理由（最上位の code）は画面の言語で出す。
      const deviceReasons = deviceAssignmentMessages(error, {
        inUse: t('ui.device-in-use'),
        notAvailable: t('ui.device-not-available'),
      });
      if (isAssignmentConflict(error)) {
        // 他の管理者が先に割当を変えていた。割当の基準だけを読み直し（ほかの入力は
        // 保つ）、機器欄は今の割当に戻る。「読み直した」と知らせるのは成功したときだけ。
        const reloaded = selectedDriver ? await loadAssignedBase(selectedDriver) : 'failed';
        if (reloaded === 'loaded') {
          showMessages([t('ui.assignment-conflict')]);
        } else if (reloaded === 'failed') {
          // 機器欄に理由と「再読み込み」が出る。
          showMessages([t('ui.assignment-conflict-reload-failed')]);
        }
      } else if (deviceReasons) {
        showMessages(deviceReasons);
      } else {
        showSaveError(error);
      }
      // ADM-14。他の管理者の変更で機器の状態が変わっている可能性がある。
      // 画面の値ではなく、サーバーの確定状態を取り直す。
      onChanged?.();
      fetchDeviceData();
      return false;
    }
  };

  useEffect(() => {
    fetchDeviceData();
  }, []);

  // 機器一覧の読み込みの順番。最初の読み込みと保存失敗後の読み直しが重なったとき、
  // 遅れた古い応答で候補を上書きしない。
  const deviceRequest = useRef(0);

  const fetchDeviceData = async () => {
    const request = ++deviceRequest.current;
    try {
      // BE-21。page_size=0では全件が返らないため、ページを順に読む。
      // 候補は編集する運転者で変わる（その運転者が使っている機器も含める）ため、
      // 一覧のまま持ち、編集画面を開いたときに絞る。
      const list = await fetchAll<any>('/v1/devices/');
      if (request !== deviceRequest.current) return;
      setDevices(Array.isArray(list) ? list : []);
      setDevicesError(null);
    } catch (error) {
      if (request !== deviceRequest.current) return;
      console.error('Failed to fetch data', error);
      // PR #253 の Codex の P2。読めなかったときは前回の候補を残さない（古い候補を選べないようにする）。
      setDevices([]);
      setDevicesError(error);
    }
  };

  /** 割当済みの機器。読めなかったときは null（「割当なし」と区別する）。 */
  const fetchAssignedDevices = async (
    driverId: number
  ): Promise<{ name: string; id: number }[] | null> => {
    const apiUrl = `/v1/devices/?user=${driverId}`;

    try {
      const response = await axiosInstance.get(apiUrl);

      return response.data.results.map((device: any) => ({
        name: device.name,
        id: device.id,
      }));
    } catch (error) {
      console.error('Failed to fetch assigned devices', error);
      return null;
    }
  };

  // ADM-14 / BE-02。「利用可能」の機器と、この運転者が使っている機器だけを候補にする。
  const deviceOptions = selectedDriver
    ? assignableDevices(devices, selectedDriver.id).map((device: any) => ({
        name: device.name,
        id: device.id,
      }))
    : [];

  const editFields = selectedDriver
    ? [
        {
          label: `${t('edit.driver-id')}`,
          name: 'id',
          type: 'text',
          value: selectedDriver.id.toString(),
          readOnly: true,
          required: true,
        },
        {
          label: `${t('edit.driver-name')}`,
          name: 'name',
          type: 'text',
          value: selectedDriver.name,
          required: true,
        },
        {
          label: `${t('edit.email')}`,
          name: 'email',
          type: 'email',
          value: selectedDriver.email,
          required: true,
        },
        {
          label: `${t('edit.dob')}`,
          name: 'birthday',
          type: 'calendar',
          value: selectedDriver.birthday,
        },
        {
          label: `${t('edit.assigned-device')}`,
          name: 'device',
          type: 'multi-select',
          options: deviceOptions,
          value: assignedDevices.map((device) =>
            JSON.stringify({ name: device.name, id: device.id })
          ),
          // 割当済みの機器を読めるまで（読めなかったときも）選ばせない。
          readOnly: assignedStatus !== 'loaded',
          // EV-318。候補（機器の一覧）を読めなかったときも、既存の文言と失敗の種類を出す。
          note:
            assignedStatus === 'failed'
              ? t('ui.assigned-devices-load-failed')
              : devicesError
              ? [t('ui.fetch-failed', { ns: 'common' }), ...classifiedMessages(devicesError, '')]
                  .filter(Boolean)
                  .join(' ')
              : undefined,
          // 読めなかったときは、編集画面を開いたまま割当だけを読み直せる。
          noteAction:
            assignedStatus === 'failed'
              ? {
                  label: t('ui.retry', { ns: 'common' }),
                  onClick: () => {
                    loadAssignedBase(selectedDriver);
                  },
                }
              : devicesError
              ? { label: t('ui.retry', { ns: 'common' }), onClick: () => fetchDeviceData() }
              : undefined,
        },
        {
          label: `${t('edit.contact')}`,
          name: 'phone',
          type: 'text',
          value: selectedDriver.phone,
        },
      ]
    : [];

  const formatDate = (datetime: string) => ({
    // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
    time: formatBusiness(datetime, 'HH:mm'),
    date: formatBusiness(datetime, i18n.language === 'jp' ? 'yyyy/MM/dd' : 'dd MMM yyyy'),
  });

  return (
    <section className="flex flex-col bg-white rounded-md p-2">
      {/* Table Header */}
      <div className="flex flex-row bg-primary/50 rounded-md py-2 pl-2 pr-4 items-center gap-2 justify-between">
        <h6 className="w-1/12 text-left">{t('table.driver-id')}</h6>
        <h6 className="w-3/12 text-left">{t('table.driver-name')}</h6>
        <h6 className="w-1/12 text-center">{t('table.exceed-limits')}</h6>
        <h6 className="w-2/12 text-center">{t('table.contact')}</h6>
        <h6 className="w-2/12 text-left">{t('table.email')}</h6>
        <h6 className="w-2/12 text-center">{t('table.registered')}</h6>
        <h6 className="w-1/12 text-center">{t('table.action')}</h6>
      </div>
      {/* Table Body */}
      <div className="flex flex-col">
        {driver.map((drv) => (
          <div
            key={drv.id}
            className="flex flex-row py-2 pl-2 pr-4 border-b items-center justify-center gap-2"
          >
            <span className="w-1/12 text-left text-gray-500 py-3">
              #{drv.id}
            </span>
            {/* EV-315。表の欄の中の空（名前・電話番号・メール）は「-」（EV-311。以前は空白）。 */}
            <span className="w-3/12 text-left">{drv.name || '-'}</span>
            <span
              className={`w-1/12 text-center rounded-md py-1 ${
                drv.exceed !== 0
                  ? 'bg-[#FFE5EE] text-[#DE0000]'
                  : 'text-green-600 bg-primary/50'
              }`}
            >
              {drv.exceed !== 0
                ? `${drv.exceed} ${t('table.time')}`
                : t('table.na')}
            </span>
            <span className="w-2/12 text-center">{drv.phone || '-'}</span>
            <span
              className="w-2/12 text-left"
              style={{
                wordWrap: 'break-word',
                maxHeight: '3rem',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {drv.email || '-'}
            </span>
            <span className="w-2/12 text-center">
              {formatDate(drv.create_time).date}
            </span>
            <div className="w-1/12 text-center flex gap-3 justify-center text-neutral/100 cursor-pointer">
              <button
                type="button"
                className="inline-flex items-center"
                aria-label={t('ui.view', { ns: 'common' })}
                onClick={() => handleViewClick(drv.id)}
              >
                <IconEye
                  size={20}
                  stroke={1.5}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className="inline-flex items-center"
                aria-label={t('ui.edit', { ns: 'common' })}
                onClick={() => handleEditClick(drv)}
              >
                <IconEdit
                  size={20}
                  stroke={1.5}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        ))}
      </div>
      {selectedDriver && (
        <EditPopup
          title={t('edit.title')}
          fields={editFields}
          save={t('edit.save')}
          initialValues={selectedDriver}
          onSave={handleSave}
          onClose={handleClosePopup}
        />
      )}
      {saveErrorPopup}
    </section>
  );
};

export default DriverTable;